import React, { useEffect } from 'react';
import { View } from 'react-native';
import { Tabs, useRouter } from 'expo-router';
import { LayoutDashboard, BarChart3, Activity, MessageSquare, Users2, User, Flag, DollarSign, Timer, Gauge } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { usePhoneShell } from '@/lib/useShell';
import { useAuth } from '@/lib/supabase';
import { useProfileStore } from '@/lib/state/profile-store';
import { useUserRole } from '@/lib/auth/role';
import { resolveGuardState } from '@/lib/auth/guard-state';
import { useAALGate } from '@/lib/auth/aal';
import { AuthGuardError } from '@/components/AuthGuardError';
import { mode } from '@/lib/config/app-mode';

export default function AdminTabLayout() {
  const { user, isLoading: isAuthLoading } = useAuth();
  // Persisted store used only in mock mode; production reads server-fetched profiles.role
  // via useUserRole. This prevents devtools-edits of localStorage from unlocking the
  // admin shell (SEC-FE-02 / RM-T1-18).
  const persistedRole = useProfileStore((s) => s.userRole);
  const { data: serverRole, isLoading: isRoleLoading, isError: isRoleError, refetch: refetchRole } = useUserRole(user?.id);
  const router = useRouter();
  const colors = useThemeColors();
  const phoneShell = usePhoneShell();

  // Wait for auth + server role fetch to finish before checking authorization to avoid
  // navigating before the Root Layout mounts (Expo Router race condition).
  const isLoading = isAuthLoading || isRoleLoading;
  const hasAuth = !!user || mode.isMock;
  const effectiveRole = mode.isMock ? persistedRole : serverRole;
  const isAuthorized = hasAuth && effectiveRole === 'admin';
  // AAL2 boundary (SEC-AUTH-02c): password login mints a REAL AAL1 session
  // before MFA is verified, so role alone is not enough — an enrolled user who
  // skipped /mfa-verify was previously inside the app on an AAL1 token. Users
  // with no MFA factor report 'satisfied' and are unaffected.
  const { status: aal, retry: retryAal } = useAALGate();
  const guardState = resolveGuardState({ isLoading, isError: isRoleError, isAuthorized, aal });

  useEffect(() => {
    if (guardState === 'redirect') {
      // An AUTHENTICATED user who simply is not an admin must not be sent to
      // /login: login redirects a live session straight back to '/', which
      // routes here again — an infinite loop that surfaced as the generic
      // ErrorBoundary screen rather than a denial (staging, 2026-08-07). '/'
      // now resolves admin canonically too, so it is a terminal destination.
      // Only a genuinely signed-out visitor belongs at /login.
      router.replace(hasAuth ? '/' : '/login');
    } else if (guardState === 'step-up-required') {
      // Enrolled but still AAL1. Send them to complete the step-up rather than
      // to /login — they have already proven their password, and bouncing them
      // to a fresh sign-in would just mint another AAL1 session and loop.
      router.replace('/mfa-verify');
    }
  }, [guardState, hasAuth]); // eslint-disable-line react-hooks/exhaustive-deps

  // Transient role-fetch error → offer a retry instead of bouncing the user out.
  // Retry must re-run BOTH probes. The AAL probe is a separate React Query
  // entry, so refetching only the role left a failed AAL result stale — the
  // guard kept deriving 'error' from it and the screen could never recover.
  if (guardState === 'error') {
    return <AuthGuardError onRetry={() => { refetchRole(); retryAal(); }} />;
  }
  if (guardState !== 'authorized') return <View style={{ flex: 1 }} />;
  return (
    <Tabs
      screenOptions={{
        headerShown: false,
        // The tab bar is the navigation owner for every PHONE-SHAPED viewport —
        // native and phone web alike — and yields to the sidebar above the tablet
        // breakpoint. It used to key off `isWeb`, which hid it at every web width
        // while the sidebar only mounts at >= 768px, so phone web had no
        // navigation at all. Reusing this same navigator (rather than building a
        // parallel web nav) is what keeps `Tabs.Protected` gating, `href: null`
        // hiding and the unread badges identical on both platforms.
        tabBarStyle: phoneShell
          ? {
              backgroundColor: colors.surface.primary,
              borderTopColor: colors.border.light,
              borderTopWidth: 1,
              height: 85,
              paddingTop: 8,
            }
          : { display: 'none' },
        tabBarActiveTintColor: colors.primary[600],
        tabBarInactiveTintColor: colors.light[500],
        tabBarLabelStyle: {
          fontSize: 10,
          fontWeight: '600',
          marginTop: 2,
        },
      }}
    >
      <Tabs.Screen
        name="admin-dashboard"
        options={{
          title: 'Dashboard',
          tabBarIcon: ({ color, size }) => <LayoutDashboard size={size - 2} color={color} />,
        }}
      />
      <Tabs.Screen
        name="executive-dashboard"
        options={{
          title: 'Analytics',
          tabBarIcon: ({ color, size }) => <BarChart3 size={size - 2} color={color} />,
        }}
      />
      <Tabs.Screen
        name="feedback"
        options={{
          title: 'Feedback',
          tabBarIcon: ({ color, size }) => <MessageSquare size={size - 2} color={color} />,
        }}
      />
      <Tabs.Screen
        name="users"
        options={{
          title: 'Users',
          tabBarIcon: ({ color, size }) => <Users2 size={size - 2} color={color} />,
        }}
      />
      <Tabs.Screen
        name="profile"
        options={{
          title: 'Profile',
          tabBarIcon: ({ color, size }) => <User size={size - 2} color={color} />,
        }}
      />
      {/* ---------------------------------------------------------------------
          NOT IN THE BAR — reached from the dashboard's Tools section, and from
          the sidebar on web (ADMIN_NAV already lists all of them).

          WHY. This group had TEN visible tabs against an app convention of four
          to five ((startup) hides 12 of 16, (investor) 8 of 13). On a 375pt
          screen ten tabs is ~37pt each — under the 44pt minimum touch target —
          with 10pt labels like "Executive" and "Retention" crushed beneath the
          icons. `href: null` removes a screen from the BAR only; every route
          stays navigable, so deep links and router.push are unaffected.
          --------------------------------------------------------------------- */}
      <Tabs.Screen name="product-analytics" options={{ href: null, title: 'Product' }} />
      <Tabs.Screen name="feature-flags" options={{ href: null, title: 'Flags' }} />
      {/* Beta-only: DELETE this line when beta ends (see src/app/(admin)/waitlist.tsx) */}
      <Tabs.Screen name="waitlist" options={{ href: null, title: 'Waitlist' }} />
      <Tabs.Screen name="data-retention" options={{ href: null, title: 'Retention' }} />
      <Tabs.Screen name="erasure-requests" options={{ href: null, title: 'Erasure' }} />
      <Tabs.Screen name="ai-cost" options={{ href: null, title: 'AI Cost' }} />
      <Tabs.Screen name="ai-quality" options={{ href: null, title: 'AI Quality' }} />
      <Tabs.Screen name="feedback-detail" options={{ href: null, title: 'Feedback Detail' }} />
      <Tabs.Screen name="user-detail" options={{ href: null, title: 'User Detail' }} />
    </Tabs>
  );
}
